// QC Audits landing — templates you can run + recent runs. qc.manage only
// (route guarded in App; API enforces regardless). Born from the helpdesk
// prod cutover: "Start full helpdesk audit" for David/QC to sweep the
// whole feature inventory, with failures auto-filing linked cases.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import type { QcAuditRun, QcAuditTemplate, QcEnvironment, QcPeopleMap } from '@itms-qc/shared';
import { auditApi } from '../lib/api';
import { useApiToken } from '../auth/useApiToken';
import { personLabel, relativeTime } from '../lib/format';
import { useMe } from '../lib/me';

const ENVIRONMENTS: QcEnvironment[] = ['prod', 'test', 'dev'];

const card: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #E2E8F0',
  borderRadius: 10,
  padding: 16,
};

function ProgressBar({ run }: { run: QcAuditRun }) {
  const done = run.passCount + run.failCount + run.blockedCount;
  const pct = run.totalItems ? Math.round((done / run.totalItems) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 7, background: '#EDF2F7', borderRadius: 4, overflow: 'hidden', display: 'flex' }}>
        <div style={{ width: `${(run.passCount / Math.max(run.totalItems, 1)) * 100}%`, background: '#2F855A' }} />
        <div style={{ width: `${(run.failCount / Math.max(run.totalItems, 1)) * 100}%`, background: '#C53030' }} />
        <div style={{ width: `${(run.blockedCount / Math.max(run.totalItems, 1)) * 100}%`, background: '#B7791F' }} />
      </div>
      <span style={{ fontSize: 12, color: '#5A6B7C', whiteSpace: 'nowrap' }}>{pct}%</span>
    </div>
  );
}

export function AuditsPage() {
  const getToken = useApiToken();
  const navigate = useNavigate();
  const me = useMe();
  const [templates, setTemplates] = useState<QcAuditTemplate[]>([]);
  const [runs, setRuns] = useState<QcAuditRun[]>([]);
  const [people, setPeople] = useState<QcPeopleMap>({});
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState<number | null>(null);
  const [env, setEnv] = useState<QcEnvironment>('prod');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const [t, r] = await Promise.all([auditApi.listTemplates(token), auditApi.listRuns(token)]);
        if (!cancelled) {
          setTemplates(t.templates);
          setRuns(r.runs);
          setPeople(r.people ?? {});
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load audits');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  async function start(templateId: number) {
    setStarting(templateId);
    setError(null);
    try {
      const token = await getToken();
      const run = await auditApi.startRun(token, templateId, env);
      navigate(`/audits/runs/${run.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start run');
      setStarting(null);
    }
  }

  if (loading) return <div style={{ padding: 24, color: '#5A6B7C' }}>Loading audits…</div>;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', display: 'grid', gap: 16 }}>
      <div>
        <h1 style={{ fontSize: 22, margin: 0 }}>Audits</h1>
        <p style={{ color: '#5A6B7C', marginTop: 4, fontSize: 14 }}>
          Systematic verification of a module's full feature inventory. Failing an item files a linked QC
          case automatically.
        </p>
      </div>

      {error && (
        <div style={{ ...card, borderColor: '#FEB2B2', background: '#FFF5F5', color: '#C53030' }}>{error}</div>
      )}

      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <h2 style={{ fontSize: 15, margin: 0 }}>Templates</h2>
          <label style={{ fontSize: 13, color: '#5A6B7C' }}>
            Target environment{' '}
            <select value={env} onChange={(e) => setEnv(e.target.value as QcEnvironment)} style={{ marginLeft: 4 }}>
              {ENVIRONMENTS.map((e2) => (
                <option key={e2} value={e2}>
                  {e2}
                </option>
              ))}
            </select>
          </label>
        </div>
        {templates.length === 0 && <div style={{ color: '#5A6B7C', fontSize: 14 }}>No audit templates yet.</div>}
        {templates.map((t) => (
          <div
            key={t.id}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px 0',
              borderTop: '1px solid #EDF2F7',
              gap: 12,
            }}
          >
            <div>
              <div style={{ fontWeight: 600 }}>
                {t.name} <span style={{ color: '#5A6B7C', fontWeight: 400 }}>v{t.version}</span>
              </div>
              <div style={{ fontSize: 13, color: '#5A6B7C' }}>
                {t.moduleName} · {t.itemCount} checks
              </div>
            </div>
            <button
              onClick={() => start(t.id)}
              disabled={starting !== null}
              style={{
                background: '#001C3E',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '9px 16px',
                fontSize: 13,
                fontWeight: 600,
                cursor: starting === null ? 'pointer' : 'default',
                opacity: starting !== null && starting !== t.id ? 0.6 : 1,
              }}
            >
              {starting === t.id ? 'Starting…' : `Start ${t.moduleName} audit`}
            </button>
          </div>
        ))}
      </div>

      <div style={card}>
        <h2 style={{ fontSize: 15, margin: '0 0 10px' }}>Recent runs</h2>
        {runs.length === 0 && <div style={{ color: '#5A6B7C', fontSize: 14 }}>No runs yet.</div>}
        {runs.map((r) => (
          <div
            key={r.id}
            onClick={() => navigate(`/audits/runs/${r.id}`)}
            style={{
              padding: '10px 0',
              borderTop: '1px solid #EDF2F7',
              cursor: 'pointer',
              display: 'grid',
              gap: 6,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ fontWeight: 600 }}>
                Run #{r.id} — {r.templateName}{' '}
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    borderRadius: 4,
                    padding: '2px 7px',
                    marginLeft: 6,
                    background: r.status === 'completed' ? '#E2F3E9' : r.status === 'abandoned' ? '#EDF2F7' : '#EBF4FF',
                    color: r.status === 'completed' ? '#0B6B3A' : r.status === 'abandoned' ? '#5A6B7C' : '#0B5FA5',
                  }}
                >
                  {r.status.replace('_', ' ')}
                </span>
              </div>
              <div style={{ fontSize: 13, color: '#5A6B7C' }}>
                {r.targetEnvironment} · started {relativeTime(r.startedAt)} by{' '}
                {personLabel(r.startedBy, me?.oid ?? '', people, { selfName: me?.displayName })}
              </div>
            </div>
            <ProgressBar run={r} />
            <div style={{ fontSize: 12, color: '#5A6B7C' }}>
              ✓ {r.passCount} pass · ✗ {r.failCount} fail · ⊘ {r.blockedCount} blocked · {r.pendingCount} pending
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
